import Paths from "./Paths";
import OtpData from "@/app/model/OtpData";
import { getApiKey } from "@/app/utils/EnvConfig";
import { ITalkie, Talkie } from "@hc-hefaistos/hc-talk-talk";
import AuthorizationKeyRequest from "@/app/dto/AuthorizationKeyRequest";
import IAllowedFactorsResponse from "@/app/dto/IAllowedFactorsResponse";
import ICreateKeyResponse from "@/app/dto/ICreateKeyResponse";
import IInitPasswordRenewalResponse from "@/app/dto/IInitPasswordRenewalResponse";
import InitPasswordRenewalRequest from "@/app/dto/InitPasswordRenewalRequest";
import IOtpResponse from "@/app/dto/IOtpResponse";
import SetPasswordRequest from "@/app/dto/SetPasswordRequest";
import BasePath from "@/app/service/api/BasePath";

class Api {
    private readonly apiClient: ITalkie;

    constructor() {
        this.apiClient = new Talkie();
    }

    public async initPasswordRenewal(request: InitPasswordRenewalRequest): Promise<IInitPasswordRenewalResponse | null> {
        const headers = Object.assign(this.getApiKeyHeaders(), this.getConsumerContextHeaders());
        return await this.apiClient.post<InitPasswordRenewalRequest, IInitPasswordRenewalResponse>(
            Paths.initPasswordRenewal,
            request,
            headers
        );
    }

    public async getAllowedFactors(customerUuid: string): Promise<IAllowedFactorsResponse | null> {
        const headers = Object.assign(
            this.getApiKeyHeaders(),
            this.getConsumerContextHeaders(),
            this.getAuthorizationHeaders(customerUuid)
        );

        return this.apiClient.get<IAllowedFactorsResponse>(Paths.getAvailableFactors, undefined, headers);
    }

    public async authorizationKey(
        customerUuid: string,
        request: AuthorizationKeyRequest
    ): Promise<ICreateKeyResponse | null> {
        const headers = Object.assign(
            this.getApiKeyHeaders(),
            this.getConsumerContextHeaders(),
            this.getAuthorizationHeaders(customerUuid)
        );

        return await this.apiClient.post<AuthorizationKeyRequest, ICreateKeyResponse>(
            Paths.authorizationsKeys,
            request,
            headers
        );
    }

    public async sendOtp(customerUuid: string, authorizationKey: string): Promise<IOtpResponse | null> {
        const headers = Object.assign(
            this.getApiKeyHeaders(),
            this.getConsumerContextHeaders(),
            this.getAuthorizationHeaders(customerUuid)
        );

        return await this.apiClient.post<object, IOtpResponse>(new BasePath(`client/v5/external/authorizations/keys/${authorizationKey}/otp/send`), {}, headers);
    }

    public async setPassword(
        customerUuid: string,
        authKey: string,
        otp: OtpData,
        request: SetPasswordRequest
    ): Promise<void | null> {
        const headers = Object.assign(
            this.getApiKeyHeaders(),
            this.getConsumerContextHeaders(),
            this.getAuthorizationHeaders(customerUuid, authKey),
            this.getAuthorizationDataHeaders(otp)
        );

        return await this.apiClient.post<SetPasswordRequest, void>(
            Paths.setPassword,
            request,
            headers
        );
    }

    private getConsumerContextHeaders(): { [fieldName: string]: string } {
        return {
            "X-Consumer-Context": "channel:WEB_LOGIN|process:PASSWORD_RENEWAL"
        };
    }

    private getApiKeyHeaders(): { [fieldName: string]: string } {
        return {
            apiKey: getApiKey()
        };
    }

    private getAuthorizationHeaders(resourceId: string, key?: string): { [fieldName: string]: string } {
        let keyParameterString: string = !!key ? `|key:${key}` : "";

        return {
            "X-Authorization": `auth_version:3.0|resource_id:${resourceId}|resource_type:CUSTOMER${keyParameterString}`
        };
    }

    private getAuthorizationDataHeaders(otp: OtpData): { [fieldName: string]: string } {
        return {
            "X-Authorization-Data": `otp_code:${otp.code}|otp_key:${otp.key}`
        };
    }
}

export default new Api();
